
/// <amd-module name="Environments/CreatePanel/Components/SelectResourceComponent" />
import * as React from "react";
import { EnvironmentResourceType } from "DistributedTask/Client/Contracts/TaskAgent";

import { VssComponent, IVssComponentProperties } from "VSS/Platform/Layout";
import * as Resources from "Environments/CreatePanel/Resources";
import { getAssetLocation } from "VSS/Platform/Location";
import { IResource } from "Environments/CreatePanel/Common/Utils";
import { Image } from "VSSUI/Image";
import { RadioButtonGroup, RadioButton } from "VSSUI/RadioButton";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { getSafeId } from "VSSUI/Util";
import { focusAsync, IFocusable } from "VSSUI/Utilities/Focus";

interface ISelectResourceComponentProperties extends IVssComponentProperties {
    onSelectionResourceType: (selectedResourceType: EnvironmentResourceType) => void;
    selectedResourceType: string | undefined;
    resources: IResource[];
    requiresFocus?: boolean;
}

export class SelectResourceComponent extends VssComponent<ISelectResourceComponentProperties, {}> {

    public render(): JSX.Element {
        return (
            <FormItem className="form-label-margin body-m" label={Resources.Resource}>
                <RadioButtonGroup
                    className="resource-radio-button-group"
                    selectedButtonId={this.props.selectedResourceType ? this.props.selectedResourceType : "-1"}
                    onSelect={(selectedButtonId: string) =>
                        this.props.onSelectionResourceType(this._getResourceType(selectedButtonId))}
                >
                    {this._getRadioButtons()}
                </RadioButtonGroup>
            </FormItem>
        );
    }

    public componentDidMount(): void {
        if (this.props.requiresFocus) {
            const radioButtonTypeString = this.props.selectedResourceType ? this.props.selectedResourceType : "-1";
            const selectedResource: IResource | undefined = this.props.resources.find((resource: IResource) => { return resource.resourceType.toString() === radioButtonTypeString });
            if (selectedResource) {
                focusAsync(this._radioButtonRefs[radioButtonTypeString].current as IFocusable<RadioButton>);
            }
        }
    }

    private _getResourceType(selectedButtonId: string): EnvironmentResourceType {
        const selectedType = this.props.resources.find((resource: IResource) => { return resource.resourceType.toString() === selectedButtonId });
        return selectedType!.resourceType;
    }

    private _getRadioButtons(): JSX.Element[] {
        let options: JSX.Element[] = [];
        this.props.resources.forEach((resource: IResource) => {
            options.push(this._getRadioButton(resource));
        });
        return options;
    }

    private _getRadioButton(resource: IResource): JSX.Element {
        let image: JSX.Element;
        if (resource.isIcon) {
            image = Icon({ iconName: resource.imageLocation });
        }
        else {
            let imageAltText = resource.imageAltText? resource.imageAltText : resource.name;
            image = Image({ src: getAssetLocation(this.context.pageContext, resource.imageLocation), alt: imageAltText,  className: "resource-icon-margin", width: 32, height: 32 });
        }

        const radioButtonTypeString = resource.resourceType.toString();
        const radioButtonAriaLabelId = "resource-radio-button-label-" + radioButtonTypeString;
        const radioButtonAriaLabel = Resources.Resource + " " + resource.name;

        if (!this._radioButtonRefs[radioButtonTypeString]) {
            this._radioButtonRefs[radioButtonTypeString] = React.createRef();
        }

        return (
            <RadioButton
                id={radioButtonTypeString}
                key={radioButtonTypeString}
                ref={this._radioButtonRefs[radioButtonTypeString]}
                ariaLabelledBy={radioButtonAriaLabelId}
                ariaDescribedBy={resource.name}
                className="resource-radio-button">
                <div className="radio-button-children-padding flex-row flex-center" id={ getSafeId(radioButtonAriaLabelId) } aria-label={radioButtonAriaLabel}>
                    {image}
                    <div >
                        <div className="body-m fontWeightSemiBold">{resource.name}</div>
                        <div className="body-s secondary-text">{resource.description}</div>
                    </div>
                </div>
            </RadioButton>
        );
    }

    private _radioButtonRefs: { [id: string]: React.RefObject<RadioButton> } = {};
}
